import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  IColumn,
  MessageBarType,
  Selection,
  SelectionMode,
} from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  buttonStylesPrimary,
  ConfirmDialog,
  DetailsList,
  Item,
  MessageBar,
  Search,
  useTheme,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  GetMembersRequest,
  RemoveMemberRequest,
} from '../../../../../admin-center/gen/user_management/user_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { messageBarContainerStyles } from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import { OktaStatusIdpLabel, OktaStatusLabel, UMUserTableColumnKey, UMUserTableColumnName } from '../../../constants';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import StatsCard from '../../StatsCard/StatsCard';
import AddAsAdminConfirmation from '../AddAsAdminConfirmation/AddAsAdminConfirmation';
import ResendEmailConfirmation from '../ResendEmailConfirmation/ResendEmailConfirmation';
import {
  actionButtonContainerStyles,
  actionContainerStyles,
  activeUserStyles,
  appleStyles,
  deactivatedUserStyles,
  githubStyles,
  googleStyles,
  gridColumnStyles,
  lockedOutUserStyles,
  oktaStyles,
  passwordExpiredUserStyles,
  passwordResetUserStyles,
  pendingActionUserStyles,
  saml2Styles,
  searchStyle,
  stagedUserStyles,
  suspendedUserStyles,
  tableContainerStyles,
  unkownSourceStyles,
  unkownUserStatusStyles,
  userManagementTableStyles,
} from '../UserManagement.styles';

interface UserProfile {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  id: string | undefined;
  status: string | undefined;
  login: string | undefined;
  profileSource: string | undefined;
}

interface TableItem {
  userName: string;
  userEmail: string;
  userStatus: string;
  userSource: string;
}

interface UserTableProps {
  onSubmitSuccess?: () => Promise<void>;
  envId: string | null;
  refreshKey: number;
}

const dummyList = getDummyList<TableItem>(10);

export default function UserTable({ onSubmitSuccess, envId, refreshKey }: UserTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [tableSelections, setTableSelections] = useState<TableItem[]>([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  const [showResendEmailConfirmationDialog, setShowResendEmailConfirmationDialog] = useState<boolean>(false);
  const [showAddAsAdminsConfirmationDialog, setShowAddAsAdminsConfirmationDialog] = useState<boolean>(false);
  const [isDeletionPending, setIsDeletionPending] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showResponseMessage, setShowResponseMessage] = useState<boolean>(false);

  const fetchUsers = async () => {
    if (!envId) return;
    setIsLoading(true);
    try {
      const req: GetMembersRequest = { groupName: `hadc_${envId}_user` };
      const response = await service.getMembers(req);
      const usersData =
        response.members?.map((member) => ({
          firstName: member.firstName ?? 'Unknown',
          lastName: member.lastName ?? 'Unknown',
          email: member.email ?? 'Unknown',
          id: member.id ?? 'Unknown',
          status: member.status ?? 'Unknown',
          login: member.login ?? 'Unknown',
          profileSource: member.profileSource ?? 'Unknown',
        })) || [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching User data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [envId, refreshKey]);

  const handleRefresh = async () => {
    await fetchUsers();
    if (onSubmitSuccess) {
      await onSubmitSuccess();
    }
  };

  const selectionRef = useRef<Selection>(
    new Selection({
      onSelectionChanged: () => {
        const selectedItems = selectionRef.current.getSelection();
        handleSelectionChanged(selectedItems);
      },
      getKey: (item: any) => item.userEmail,
    })
  );

  const handleSelectionChanged = useCallback((items: any[]) => {
    setTableSelections(items);
  }, []);

  const userData = users.map((user) => ({
    userName: `${user.firstName || ''} ${user.lastName || ''}`,
    userStatus:
      (user.status?.endsWith('-IDP') ? OktaStatusIdpLabel[user.status] : OktaStatusLabel[user.status ?? '']) ||
      'Unknown',
    userEmail: user.email || 'Unknown',
    userSource: user.profileSource || 'Unknown',
  }));

  useEffect(() => {
    setTableSelections([]);
    if (selectionRef.current) {
      selectionRef.current.setAllSelected(false);
    }
  }, [refreshKey]);

  const handleExport = () => {
    const exportData = tableSelections
      .map((member) => {
        return `Name: ${member.userName}\nEmail: ${member.userEmail}\nStatus: ${member.userStatus}\n`;
      })
      .join('\n');

    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    setIsDeletionPending(true);
    const selectedUsers = tableSelections;
    const membersList = selectedUsers
      .filter((selectedUser) => user?.name !== selectedUser.userEmail)
      .map((selectedUser) => {
        const [firstName, ...lastNameParts] = selectedUser.userName.split(' ');
        return {
          firstName: firstName || '',
          lastName: lastNameParts.join(' ') || '',
          email: selectedUser.userEmail,
        };
      });

    if (membersList.length !== selectedUsers.length) {
      setResponseMessage('❌ Self-deletion is not allowed');
    }

    try {
      const userContext = await buildUserContext(user);
      const request: RemoveMemberRequest = {
        members: membersList,
        groupNames: envId ? [`hadc_${envId}_user`] : [],
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.removeMembers(request);

      if (response.statusUpdate) {
        setResponseMessage(response.statusUpdate);
      } else {
        setResponseMessage('❌ Error: Failed to delete');
      }

      await handleRefresh();
    } catch (error) {
      setResponseMessage(`❌ Network error: ${error}`);
    } finally {
      setIsDeletionPending(false);
      setShowDeleteConfirmationDialog(false);
      setShowResponseMessage(true);
    }
  };

  const filteredData = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    return userData.filter(
      (item) =>
        item.userName.toLowerCase().includes(searchTerm) ||
        item.userEmail.toLowerCase().includes(searchTerm) ||
        item.userStatus.toLowerCase().includes(searchTerm) ||
        item.userSource.toLowerCase().includes(searchTerm)
    );
  }, [userData, searchQuery]);

  const columns: IColumn[] = [
    {
      key: UMUserTableColumnKey.name,
      fieldName: UMUserTableColumnKey.name,
      name: UMUserTableColumnName.name,
      minWidth: 150,
      maxWidth: 200,
      onRender: (item: TableItem) => <>{!isLoading ? item.userName : <SkeletonLoader width="65%" height="50%" />}</>,
    },
    {
      key: UMUserTableColumnKey.email,
      fieldName: UMUserTableColumnKey.email,
      name: UMUserTableColumnName.email,
      minWidth: 200,
      maxWidth: 300,
      onRender: (item: TableItem) => <>{!isLoading ? item.userEmail : <SkeletonLoader width="65%" height="50%" />}</>,
    },
    {
      key: UMUserTableColumnKey.status,
      fieldName: UMUserTableColumnKey.status,
      name: UMUserTableColumnName.status,
      minWidth: 100,
      maxWidth: 150,
      onRender: (user: any) => {
        if (isLoading) return <SkeletonLoader width="65%" height="50%" />;
        const statusStylesMap = new Map([
          ['Active', activeUserStyles],
          ['Active - IdP', activeUserStyles],
          ['Staged', stagedUserStyles],
          ['Staged - IdP', stagedUserStyles],
          ['Pending User Action', pendingActionUserStyles],
          ['Pending User Action - IdP', pendingActionUserStyles],
          ['Password Reset', passwordResetUserStyles],
          ['Password Reset - IdP', passwordResetUserStyles],
          ['Password Expired', passwordExpiredUserStyles],
          ['Password Expired - IdP', passwordExpiredUserStyles],
          ['Locked Out', lockedOutUserStyles],
          ['Locked Out - IdP', lockedOutUserStyles],
          ['Suspended', suspendedUserStyles],
          ['Suspended - IdP', suspendedUserStyles],
          ['Deactivated', deactivatedUserStyles],
          ['Deactivated - IdP', deactivatedUserStyles],
        ]);

        return (
          <div>
            <Item
              data={{ id: '', title: user.userStatus }}
              styles={statusStylesMap.get(user.userStatus || '') || unkownUserStatusStyles}
              idField="id"
              labelField="title"
            />
          </div>
        );
      },
    },
    {
      key: UMUserTableColumnKey.source,
      fieldName: UMUserTableColumnKey.source,
      name: UMUserTableColumnName.source,
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (user: any) => {
        if (isLoading) return <SkeletonLoader width="65%" height="50%" />;
        const statusStylesMap = new Map([
          ['SAML2', saml2Styles],
          ['GITHUB', githubStyles],
          ['GOOGLE', googleStyles],
          ['APPLE', appleStyles],
          ['OKTA', oktaStyles],
        ]);

        return (
          <div>
            <Item
              data={{ id: '', title: user.userSource }}
              styles={statusStylesMap.get(user.userSource || '') || unkownSourceStyles}
              idField="id"
              labelField="title"
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {showResponseMessage && (
        <div style={messageBarContainerStyles}>
          <MessageBar
            timeout={6000}
            onDismiss={() => setShowResponseMessage(false)}
            messageBarType={
              ['❌', 'Error', 'Failed'].some((keyword) => responseMessage?.includes(keyword))
                ? MessageBarType.error
                : MessageBarType.success
            }
          >
            {responseMessage}
          </MessageBar>
        </div>
      )}
      <StatsCard title="Total Users" stat={users.length} isLoading={isLoading} />
      <div style={gridColumnStyles()}>
        <div style={actionContainerStyles()}>
          <Search
            emptyMessage="No results found"
            placeholder="Search Users..."
            onSearchTextChange={(text: string) => setSearchQuery(text)}
            style={searchStyle}
          />
          <div style={actionButtonContainerStyles()}>
            <Button
              text="Resend Emails"
              styles={buttonStylesGhost}
              disabled={tableSelections.length === 0 || isLoading}
              onClick={() => setShowResendEmailConfirmationDialog(true)}
            />
            <Button
              text="Add to Groups"
              styles={buttonStylesPrimary}
              disabled={tableSelections.length === 0 || isLoading}
              onClick={() => setShowAddAsAdminsConfirmationDialog(true)}
            />
            <Button
              text="Export Members"
              styles={buttonStylesGhost}
              disabled={tableSelections.length === 0 || isLoading}
              onClick={handleExport}
            />
            <Button
              text="Remove"
              styles={buttonStylesDanger}
              disabled={tableSelections.length === 0 || isLoading}
              onClick={() => setShowDeleteConfirmationDialog(true)}
            />
          </div>
        </div>
        <div style={tableContainerStyles(theme)}>
          {!isLoading && users.length === 0 ? (
            <MessageBar messageBarType={MessageBarType.warning}>{'Users group is empty'}</MessageBar>
          ) : (
            <DetailsList
              columns={columns}
              selection={selectionRef.current}
              selectionPreservedOnEmptyClick={true}
              selectionMode={SelectionMode.multiple}
              checkboxVisibility={CheckboxVisibility.always}
              layoutMode={DetailsListLayoutMode.justified}
              styles={userManagementTableStyles(theme)}
              items={isLoading ? dummyList : filteredData}
              setKey="userEmail"
            />
          )}
        </div>
      </div>
      <ConfirmDialog
        hidden={!showDeleteConfirmationDialog}
        title="Confirm Delete"
        msg={
          isDeletionPending
            ? 'Deleting users...'
            : tableSelections.length === 1
            ? 'Are you sure you want to remove the selected user from User group?'
            : `Are you sure you want to remove ${tableSelections.length} users from User group?`
        }
        confirmationButtonText={isDeletionPending ? '...' : 'Yes'}
        dismissalButtonText="Cancel"
        onConfirm={handleDelete}
        onDismiss={() => {
          setShowDeleteConfirmationDialog(false);
        }}
        confirmationButtonDisabled={isDeletionPending}
      />
      <ResendEmailConfirmation
        users={tableSelections}
        showDialog={showResendEmailConfirmationDialog}
        setShowDialog={setShowResendEmailConfirmationDialog}
      />
      <AddAsAdminConfirmation
        users={tableSelections}
        showDialog={showAddAsAdminsConfirmationDialog}
        setShowDialog={setShowAddAsAdminsConfirmationDialog}
        onSubmitSuccess={handleRefresh}
        envId={envId}
      />
    </>
  );
}
